import { ReactNode } from 'react'

import { format } from 'date-fns'
import { TFunction } from 'i18next'

import { InstalledConnection, isOAuthUser } from 'app/connections/types'

import { CodeTag } from 'components'

import { maskValue } from '../../maskValue'
import styles from './Overview.module.scss'

interface AccountDetail {
  label: string
  value: ReactNode
}

const getConnectedUser = (
  connection: InstalledConnection,
  formatConnectedUser: (name: string, timestamp: string) => string,
) => {
  const user = isOAuthUser(connection.authorizationUser) ? connection.authorizationUser : null
  const userName = user?.name ? user.name : user?.email

  return userName && user?.timestamp
    ? formatConnectedUser(userName, format(new Date(user.timestamp), 'MMM d, HH:mm:ss'))
    : userName
}

const getGoogleAdsAccountDetails = (
  connection: Extract<InstalledConnection, { name: 'googleAdwords' }>,
  t: TFunction<'connections'>,
): AccountDetail[] => {
  const accounts = connection.config.accounts
    .map((account) => `${maskValue(account.id)}${account.name ? ` (${account.name})` : ''}`)
    .join(' • ')
  const connectedAs = getConnectedUser(connection, (name, timestamp) =>
    t('manage.overview.accountsAndIds.connectedAsValue', { name, timestamp }),
  )

  return [
    {
      label: t('manage.overview.accountsAndIds.googleAdwords.accounts'),
      value: accounts ? accounts : t('overview.emptyValue'),
    },
    {
      label: t('manage.overview.accountsAndIds.googleAdwords.campaignId'),
      value: connection.config.campaignId ? connection.config.campaignId : t('overview.emptyValue'),
    },
    {
      label: t('manage.overview.accountsAndIds.googleAdwords.connectedAs'),
      value: connectedAs ? connectedAs : t('overview.emptyValue'),
    },
  ]
}

const getFacebookAccountDetails = (
  connection: Extract<InstalledConnection, { name: 'facebook' }>,
  t: TFunction<'connections'>,
): AccountDetail[] => {
  const emptyValue = t('overview.emptyValue')
  const accounts = connection.config.accounts
    .map((account) => `${maskValue(account.id)}${account.name ? ` (${account.name})` : ''}`)
    .join(' • ')
  const connectedAs = getConnectedUser(connection, (name, timestamp) =>
    t('manage.overview.accountsAndIds.connectedAsValue', { name, timestamp }),
  )

  return [
    {
      label: t('manage.overview.accountsAndIds.facebook.accounts'),
      value: accounts ? accounts : emptyValue,
    },
    {
      label: t('manage.overview.accountsAndIds.facebook.pixelId'),
      value: connection.config.pixelId ? (
        <CodeTag color="grey">{maskValue(connection.config.pixelId)}</CodeTag>
      ) : (
        emptyValue
      ),
    },
    {
      label: t('manage.overview.accountsAndIds.facebook.adId'),
      value: connection.config.adId ? connection.config.adId : emptyValue,
    },
    {
      label: t('manage.overview.accountsAndIds.facebook.connectedAs'),
      value: connectedAs ? connectedAs : emptyValue,
    },
  ]
}

const getGoogleAnalyticsAccountDetails = (
  connection: Extract<InstalledConnection, { name: 'googleAnalytics' }>,
  t: TFunction<'connections'>,
): AccountDetail[] => {
  const emptyValue = t('overview.emptyValue')

  return [
    {
      label: t('manage.overview.accountsAndIds.googleAnalytics.propertyId'),
      value: connection.config.propertyId ? connection.config.propertyId : emptyValue,
    },
    {
      label: t('manage.overview.accountsAndIds.googleAnalytics.measurementId'),
      value: connection.config.measurementId ? connection.config.measurementId : emptyValue,
    },
    {
      label: t('manage.overview.accountsAndIds.googleAnalytics.apiKey'),
      value: connection.config.apiSecret ? (
        <CodeTag color="grey">{maskValue(connection.config.apiSecret)}</CodeTag>
      ) : (
        emptyValue
      ),
    },
  ]
}

const getGoogleBigqueryAccountDetails = (
  connection: Extract<InstalledConnection, { name: 'googleBigquery' }>,
  t: TFunction<'connections'>,
): AccountDetail[] => [
  {
    label: t('manage.overview.accountsAndIds.googleBigquery.project'),
    value: connection.config.project ? connection.config.project : t('overview.emptyValue'),
  },
]

const getInnkeeprAccountDetails = (
  connection: Extract<InstalledConnection, { name: 'innkeepr' }>,
  t: TFunction<'connections'>,
): AccountDetail[] => [
  {
    label: t('manage.overview.accountsAndIds.innkeepr.trackingUrl'),
    value: connection.config.trackingUrl ? connection.config.trackingUrl : t('overview.emptyValue'),
  },
]

const getShopifyAccountDetails = (
  connection: Extract<InstalledConnection, { name: 'shopify' }>,
  t: TFunction<'connections'>,
): AccountDetail[] => [
  {
    label: t('manage.overview.accountsAndIds.shopify.stores'),
    value: connection.config.shops.length ? (
      <div className={styles.stores}>
        {connection.config.shops.map((shop) => (
          <div className={styles.store} key={shop.name}>
            <CodeTag color="grey">{shop.name}</CodeTag>
            <span className={shop.pixelId ? undefined : styles.waitingForInstall}>
              {shop.pixelId
                ? t('manage.overview.accountsAndIds.shopify.installed')
                : t('manage.overview.accountsAndIds.shopify.waitingForInstall')}
            </span>
          </div>
        ))}
      </div>
    ) : (
      t('overview.emptyValue')
    ),
  },
]

const getTiktokAccountDetails = (
  connection: Extract<InstalledConnection, { name: 'tiktok' }>,
  t: TFunction<'connections'>,
): AccountDetail[] => {
  const advertisers = connection.config.advertisers
    .map(
      (advertiser) =>
        `${maskValue(advertiser.id)}${advertiser.name ? ` (${advertiser.name})` : ''}`,
    )
    .join(' • ')

  return [
    {
      label: t('manage.overview.accountsAndIds.tiktok.advertisers'),
      value: advertisers ? advertisers : t('overview.emptyValue'),
    },
  ]
}

const getWoocommerceAccountDetails = (
  connection: Extract<InstalledConnection, { name: 'woocommerce' }>,
  t: TFunction<'connections'>,
): AccountDetail[] => {
  const emptyValue = t('overview.emptyValue')

  return [
    {
      label: t('manage.overview.accountsAndIds.woocommerce.shopbaseUrl'),
      value: connection.config.shopbaseUrl ? connection.config.shopbaseUrl : emptyValue,
    },
    {
      label: t('manage.overview.accountsAndIds.woocommerce.apiKey'),
      value: connection.config.apiKey ? connection.config.apiKey : emptyValue,
    },
    {
      label: t('manage.overview.accountsAndIds.woocommerce.apiSecret'),
      value: connection.config.apiSecret ? (
        <CodeTag color="grey">{maskValue(connection.config.apiSecret)}</CodeTag>
      ) : (
        emptyValue
      ),
    },
  ]
}

export const getAccountDetails = (
  connection: InstalledConnection,
  t: TFunction<'connections'>,
): AccountDetail[] => {
  switch (connection.name) {
    case 'facebook':
      return getFacebookAccountDetails(connection, t)
    case 'googleAdwords':
      return getGoogleAdsAccountDetails(connection, t)
    case 'googleAnalytics':
      return getGoogleAnalyticsAccountDetails(connection, t)
    case 'googleBigquery':
      return getGoogleBigqueryAccountDetails(connection, t)
    case 'innkeepr':
      return getInnkeeprAccountDetails(connection, t)
    case 'shopify':
      return getShopifyAccountDetails(connection, t)
    case 'tiktok':
      return getTiktokAccountDetails(connection, t)
    case 'woocommerce':
      return getWoocommerceAccountDetails(connection, t)
    default:
      return []
  }
}

export type { AccountDetail }
